import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { type BrowseFilters, type ProgressStatus, type StatusOption } from '../BrowseCourses.types';
import { PROGRESS_OPTIONS } from '../constants';
import { FilterOptionGroup } from './FilterOptionGroup';
import '../locales/browseCourses/browseCourses-en.json';

type Props = {
    draft: BrowseFilters;
    availableTopics: string[];
    visibleStatusOptions: StatusOption[];
    showProgress: boolean;
    clearTopics: () => void;
    toggleTopic: (topic: string) => void;
    clearProgress: () => void;
    toggleProgress: (progress: ProgressStatus) => void;
    toggleStatus: (status: StatusOption) => void;
};

// The status, progress, and topic filters. Progress shows only to a signed-in user. Status shows only
// when some course has an extended status (new, updated, beta, coming soon, or elective).
export const CourseFilterGroups: FC<Props> = ({
    draft,
    availableTopics,
    visibleStatusOptions,
    showProgress,
    clearTopics,
    toggleTopic,
    clearProgress,
    toggleProgress,
    toggleStatus,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'browseCourses.browseCourses' });

    return (
        <div className="flex flex-col gap-5">
            {visibleStatusOptions.length > 0 && (
                <FilterOptionGroup
                    heading={t('filterByStatus')}
                    options={visibleStatusOptions.map(status => ({
                        id: status,
                        label: t(status),
                        selected: draft.selectedStatus === status,
                        onPress: () => toggleStatus(status),
                    }))}
                />
            )}

            {showProgress && (
                <FilterOptionGroup
                    heading={t('filterByProgress')}
                    options={[
                        {
                            id: 'any-progress',
                            label: t('anyProgress'),
                            selected: draft.progress.length === 0,
                            onPress: clearProgress,
                        },
                        ...PROGRESS_OPTIONS.map(progress => ({
                            id: progress,
                            label: t(progress),
                            selected: draft.progress.includes(progress),
                            onPress: () => toggleProgress(progress),
                        })),
                    ]}
                />
            )}

            <FilterOptionGroup
                heading={t('filterByTopic')}
                options={[
                    {
                        id: 'any-topic',
                        label: t('anyTopic'),
                        selected: draft.topics.length === 0,
                        onPress: clearTopics,
                    },
                    ...availableTopics.map(topic => ({
                        id: topic,
                        label: topic,
                        selected: draft.topics.includes(topic),
                        onPress: () => toggleTopic(topic),
                    })),
                ]}
            />
        </div>
    );
};
